/**
 * Discrete cash-game blind levels for the Table Filters drawer.
 *
 * The drawer replaced the old min/max blinds slider with a multi-select grid
 * of specific stake levels (per the Hijack Lobby design). Each level is matched
 * against a table's small blind (`sb`) — small blinds are unique across levels,
 * so `sb` alone is a reliable key.
 */

export interface BlindLevel {
  /** Canonical id persisted in filter state, e.g. '0.02/0.05'. */
  id: string;
  /** Small blind — matched against CashGame.sb. */
  sb: number;
  /** Display label, e.g. '$0.02/$0.05'. */
  label: string;
}

export const BLIND_LEVELS: BlindLevel[] = [
  { id: '0.02/0.05', sb: 0.02, label: '$0.02/$0.05' },
  { id: '0.05/0.10', sb: 0.05, label: '$0.05/$0.10' },
  { id: '0.10/0.25', sb: 0.1, label: '$0.10/$0.25' },
  { id: '0.25/0.50', sb: 0.25, label: '$0.25/$0.50' },
  { id: '0.50/1', sb: 0.5, label: '$0.50/$1' },
  { id: '1/2', sb: 1, label: '$1/$2' },
  { id: '2/5', sb: 2, label: '$2/$5' },
  { id: '5/10', sb: 5, label: '$5/$10' },
  { id: '10/25', sb: 10, label: '$10/$25' },
  { id: '25/50', sb: 25, label: '$25/$50' }
];

/**
 * Canonical id for a small blind, used as the stable key of a derived
 * BlindLevel. Known stakes keep their design id (e.g. '1/2'); any other
 * (off-grid) blind gets a stable derived id.
 */
export const blindLevelId = (sb: number): string => {
  const known = BLIND_LEVELS.find((l) => Math.abs(l.sb - sb) < 0.001);
  return known ? known.id : `sb:${sb}`;
};

/**
 * Build the blind-level filter options from the cash tables actually present in
 * the lobby — so EVERY available stake shows up, including blinds not in the
 * static design grid above. De-duplicated by small blind and sorted ascending;
 * labels come from the table's own stakes string (matching the tiles).
 */
export const deriveBlindLevels = (games: { sb: number; stakes?: string }[]): BlindLevel[] => {
  const byId = new Map<string, BlindLevel>();
  for (const g of games) {
    if (typeof g.sb !== 'number' || !Number.isFinite(g.sb)) continue;
    const id = blindLevelId(g.sb);
    if (byId.has(id)) continue;
    const known = BLIND_LEVELS.find((l) => Math.abs(l.sb - g.sb) < 0.001);
    byId.set(id, { id, sb: g.sb, label: g.stakes || known?.label || `$${g.sb}` });
  }
  return Array.from(byId.values()).sort((a, b) => a.sb - b.sb);
};

/**
 * Does a table's small blind fall into one of the selected blind levels?
 *
 * An empty selection means "no blind filter" — every table passes, so by default
 * every available stake is shown. Otherwise the table's small blind must map to a
 * selected level id. Uses blindLevelId so off-grid stakes (the dynamic options
 * from deriveBlindLevels) are matched the same way as the static design grid.
 */
export const matchesBlinds = (sb: number, selected: Set<string>): boolean => {
  if (selected.size === 0) return true;
  return selected.has(blindLevelId(sb));
};
